import { motion, AnimatePresence } from "framer-motion";
import { Check, X, ArrowClockwise, Sparkle } from "@phosphor-icons/react";
import { useAIStore } from "../../stores/ai";
import { useDocumentStore } from "../../stores/document";
import { cn } from "../../lib/cn";

function ShimmerSkeleton() {
  return (
    <div className="flex flex-col gap-1.5 py-1">
      {[80, 60, 72].map((w, i) => (
        <div
          key={i}
          className="h-3 rounded-sm"
          style={{
            width: `${w}%`,
            background:
              "linear-gradient(90deg, var(--bg-surface-hover) 25%, var(--border-subtle) 50%, var(--bg-surface-hover) 75%)",
            backgroundSize: "200% 100%",
            animation: `shimmer-sweep 1.5s ease-in-out infinite ${i * 0.15}s`,
          }}
        />
      ))}
    </div>
  );
}

function TypewriterCursor() {
  return (
    <motion.span
      className="inline-block w-[2px] h-[1em] bg-[var(--color-lever-orange)] ml-0.5 align-text-bottom rounded-full"
      animate={{ opacity: [1, 0] }}
      transition={{ duration: 0.6, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
    />
  );
}

export function AIInlineSuggestion() {
  const currentSuggestion = useAIStore((s) => s.currentSuggestion);
  const isProcessing = useAIStore((s) => s.isProcessing);
  const streamingText = useAIStore((s) => s.streamingText);
  const acceptSuggestion = useAIStore((s) => s.acceptSuggestion);
  const rejectSuggestion = useAIStore((s) => s.rejectSuggestion);
  const clearSuggestion = useAIStore((s) => s.clearSuggestion);

  const currentDocument = useDocumentStore((s) => s.currentDocument);
  const updateContent = useDocumentStore((s) => s.updateContent);

  const handleAccept = () => {
    if (!currentSuggestion || !currentDocument) return;
    const idx = currentDocument.content.indexOf(currentSuggestion.original);
    let newContent: string;
    if (idx === -1) {
      newContent = currentDocument.content;
    } else {
      newContent =
        currentDocument.content.slice(0, idx) +
        currentSuggestion.suggestion +
        currentDocument.content.slice(idx + currentSuggestion.original.length);
    }
    updateContent(newContent);
    acceptSuggestion();
    clearSuggestion();
  };

  const handleReject = () => {
    rejectSuggestion();
    clearSuggestion();
  };

  const isStreaming = isProcessing && !currentSuggestion;
  const waitingForFirstToken = isStreaming && !streamingText;
  const displayText = currentSuggestion?.suggestion ?? streamingText;
  const hasSuggestion = currentSuggestion || (isProcessing && streamingText);
  const visible = hasSuggestion || waitingForFirstToken;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 8, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.97 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className={cn(
            "relative flex flex-col gap-3 rounded-[var(--radius-xl)] p-4",
            "bg-[var(--bg-surface)] backdrop-blur-[var(--blur-md)]",
            "border border-[var(--border-subtle)]",
            isStreaming ? "shadow-[var(--glow-ai)]" : "shadow-[var(--shadow-lg)]",
          )}
        >
          {/* Header label */}
          <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
            <Sparkle
              size={10}
              weight="fill"
              className={cn(
                "text-[var(--color-lever-orange)]",
                isStreaming && "animate-pulse",
              )}
            />
            {isStreaming ? "Generating..." : "AI Suggestion"}
          </div>

          {/* Diff body */}
          <div className="flex flex-col gap-2 font-mono text-xs leading-relaxed">
            {waitingForFirstToken ? (
              <ShimmerSkeleton />
            ) : (
              <>
                {/* Original (strikethrough) */}
                {currentSuggestion?.original && (
                  <div
                    className={cn(
                      "rounded-[var(--radius-sm)] px-2 py-1.5",
                      "bg-[var(--color-soft-red)]/6 border border-[var(--color-soft-red)]/10",
                    )}
                  >
                    <span className="line-through decoration-[var(--color-soft-red)]/50 text-[var(--text-muted)]">
                      {currentSuggestion.original}
                    </span>
                  </div>
                )}

                {/* New text */}
                {displayText && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.15 }}
                    className={cn(
                      "rounded-[var(--radius-sm)] px-2 py-1.5",
                      "bg-[var(--color-signal-green)]/6 border border-[var(--color-signal-green)]/10",
                    )}
                  >
                    <span className="text-[var(--text-primary)]">
                      {displayText}
                    </span>
                    {isStreaming && <TypewriterCursor />}
                  </motion.div>
                )}
              </>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleAccept}
              disabled={isProcessing}
              className={cn(
                "flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-1.5 text-xs font-medium",
                "bg-[var(--color-signal-green)]/15 text-[var(--color-signal-green)]",
                "hover:bg-[var(--color-signal-green)]/25",
                "transition-colors duration-100",
                "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[var(--color-signal-green)]/15",
              )}
            >
              <Check size={12} weight="bold" />
              Accept
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleReject}
              disabled={isProcessing}
              className={cn(
                "flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-1.5 text-xs font-medium",
                "text-[var(--text-muted)]",
                "hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-secondary)]",
                "transition-colors duration-100",
                "disabled:opacity-40 disabled:cursor-not-allowed",
              )}
            >
              <X size={12} weight="bold" />
              Reject
            </motion.button>
            {!isProcessing && currentSuggestion && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.02, rotate: 180 }}
                whileTap={{ scale: 0.97 }}
                transition={{ duration: 0.2 }}
                onClick={() => {
                  if (!currentSuggestion) return;
                  clearSuggestion();
                }}
                className={cn(
                  "flex items-center gap-1.5 rounded-[var(--radius-md)] px-2 py-1.5 text-xs font-medium",
                  "text-[var(--text-muted)]",
                  "hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-secondary)]",
                  "transition-colors duration-100",
                )}
                title="Retry"
              >
                <ArrowClockwise size={12} weight="bold" />
              </motion.button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
